import type { Card, Position } from '@whispers/sdk';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCards, useReading, useSpreads } from '../api.js';
import { CardDetailDialog } from '../components/CardDetailDialog.js';
import { Deck } from '../components/Deck.js';
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';
import { usePreloadCardImages } from '../hooks/usePreloadCardImages.js';
import { useWindowSize } from '../hooks/useWindowSize.js';
import { indexById } from '../utils/collections.js';

export const NewReadingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, refetch } = useAuth();
  const { isPreviewMode, isLoading: previewIsLoading } = useStoryblokPreview();
  const { width, height } = useWindowSize();

  const [expandedCard, setExpandedCard] = useState<{ card: Card; position: Position } | undefined>(
    undefined,
  );
  const [showComplete, setShowComplete] = useState(false);

  // Fetch reading data — only needs spreadId and deckId
  const { data: readingData, isLoading: readingLoading } = useReading({
    readingId: id ?? '',
    enabled: !!id && isAuthenticated && !isPreviewMode,
    include: ['draws'],
  });

  // Fetch spreads with positions for the spread detail
  const { data: spreadsData, isLoading: spreadsLoading } = useSpreads({
    include: ['positions'],
    enabled: isAuthenticated && !isPreviewMode,
  });

  // Fetch cards for the deck
  const { data: cardsData, isLoading: cardsLoading } = useCards(
    { deckId: readingData?.reading.deckId, include: ['decks'] },
    { enabled: !!readingData?.reading.deckId && !isPreviewMode },
  );

  const reading = readingData?.reading;
  const draws = readingData?.draws ?? [];
  const spread = reading
    ? (spreadsData?.spreads ?? []).find((s) => s.id === reading.spreadId)
    : undefined;
  const positions = (spreadsData?.positions ?? []).filter((p) => p.spreadId === spread?.id);

  const cardsById = indexById(cardsData?.cards ?? []);
  const positionsById = indexById(positions);
  const cards = draws
    .toSorted(
      (a, b) =>
        (positionsById.get(a.positionId)?.order ?? 0) -
        (positionsById.get(b.positionId)?.order ?? 0),
    )
    .flatMap((draw) => {
      const card = cardsById.get(draw.cardId);
      return card ? [card] : [];
    });
  const deckSlug = cardsData?.decks?.[0]?.slug;
  usePreloadCardImages(cards, deckSlug);

  if (authLoading || previewIsLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated && !isPreviewMode) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (isPreviewMode) {
    return <PreviewPlaceholder />;
  }

  const isLoading = readingLoading || spreadsLoading || cardsLoading;

  if (isLoading || !width || !height) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!reading || !spread) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-text-secondary">Reading not found</p>
          <button onClick={() => void navigate('/')} className="mt-4 text-text underline">
            Go home
          </button>
        </div>
      </div>
    );
  }

  const handleExpanded = (params?: { card: Card; position: Position }) => {
    setExpandedCard(params);
  };

  const handleComplete = () => {
    setShowComplete(true);
  };

  const handleFinish = async () => {
    await refetch();
    void navigate(`/readings/${reading.id}`);
  };

  return (
    <div className="fixed inset-0 bg-background">
      {/* Spread name */}
      <div className="absolute left-4 top-4 z-10">
        <h1 className="text-lg font-medium text-text">{spread.name}</h1>
        <p className="text-sm text-text-secondary">Tap a card to draw</p>
      </div>

      {/* Card deck */}
      <Deck
        cards={cards}
        deckSlug={deckSlug ?? 'whispers-from-the-woods'}
        positions={positions}
        width={width}
        height={height}
        onExpanded={handleExpanded}
        onComplete={handleComplete}
      />

      {/* Card detail dialog */}
      <CardDetailDialog
        card={expandedCard?.card}
        deckSlug={deckSlug}
        position={expandedCard?.position}
        open={!!expandedCard}
        onClose={() => setExpandedCard(undefined)}
      />

      {/* Complete button */}
      {showComplete && (
        <button
          onClick={() => void handleFinish()}
          className="fixed bottom-10 right-10 z-20 rounded-full bg-button-solid-bg p-4 text-text-inverse shadow-lg transition-colors hover:bg-button-solid-bg-hover"
          aria-label="Complete reading"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Cancel button */}
      <button
        onClick={() => void navigate('/')}
        className="fixed bottom-10 left-10 z-20 rounded-full bg-surface p-4 text-text shadow-lg transition-colors hover:bg-surface-sunken"
        aria-label="Cancel"
      >
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};
